import React, { useMemo } from "react";
import { flexRender } from "@tanstack/react-table";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Pin } from "lucide-react";

import { SELECT_COLUMN_ID } from "constants/Constants";
import { sanitizeCSSVarName } from "components/helpers";
import { HeaderSort } from "components/BaseTableV3/HeaderSort";
import { getCommonPinningStyles } from "components/BaseTableV3/BaseTableV3";
import { isActionsColumn } from "components/BaseTableV3/table-helpers";

import styles from "components/BaseTableV3/BaseTableV3.module.scss";

export function BaseTableV3Header({ header, handleResizeColumnQuery }) {
  const isPinned = header.column.getIsPinned();
  const isSorted = !!header.column.getIsSorted();
  const showPinIcon =
    !!isPinned &&
    header.column.id !== SELECT_COLUMN_ID &&
    !isActionsColumn(header.column.columnDef);

  const sanitizedHeaderKey = useMemo(() => {
    return sanitizeCSSVarName(`--header-${header.column.id}-size`);
  }, [header.column.id]);

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    node: headerNode,
  } = useSortable({
    id: header.column.id,
    disabled: isPinned,
  });

  const style = {
    opacity: isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",
    width: `calc(var(${sanitizedHeaderKey}) * 1px)`,
    zIndex: isDragging ? 1 : 0,
    ...(isSorted && {
      backgroundColor: "var(--color-sorted-header, #e5e7eb)",
    }),
  };

  const handleResizeEnds = () => {
    if (typeof handleResizeColumnQuery === "function") {
      handleResizeColumnQuery({
        [header.column.id]: headerNode.current.getBoundingClientRect().width,
      });
    }
  };

  return (
    <th
      key={header.id}
      ref={setNodeRef}
      colSpan={header.colSpan}
      style={isPinned ? getCommonPinningStyles(header.column, true) : style}
    >
      <div className={styles.wrapper}>
        {showPinIcon && (
          <Pin
            aria-label="Frozen column"
            className="tw:shrink-0 tw:max-h-3 tw:text-[var(--color-text-muted)]"
          />
        )}
        <div
          className={styles["header-content"]}
          {...(isPinned
            ? {}
            : {
                ...attributes,
                ...listeners,
                style: {
                  padding: 0,
                  border: "none",
                  cursor: isDragging ? "grabbing" : "grab",
                },
              })}
        >
          {header.isPlaceholder
            ? null
            : flexRender(header.column.columnDef.header, header.getContext())}
        </div>
        <HeaderSort column={header.column} />
      </div>
      {header.column.getCanResize() && (
        <>
          <div
            {...{
              onDoubleClick: () => header.column.resetSize(),
              onMouseDown: header.getResizeHandler(),
              onTouchStart: header.getResizeHandler(),
              onMouseUp: handleResizeEnds,
              className: styles.resizer,
            }}
          />
        </>
      )}
    </th>
  );
}
